import { Descendant } from "slate";

import { config } from "config/config";
import { EMPTY_EDITOR_STATE } from "constants/rich-text";
import { NEW_TICKET_TEMP_ID, SESSION_STORAGE_DRAFT_KEY } from "constants/tickets/drafts";
import { agentExists } from "helpers/agent";
import { teamExists } from "helpers/team";
import { isEmail } from "helpers/validate";
import { IDraft, TicketPriority, TicketStatus } from "interfaces/entities/ticket";
import { getItem } from "services/session-storage";
import { IAgent } from "store/entities/agents";
import { IReplyAddress } from "store/entities/reply-addresses/interfaces";
import { ITeam } from "store/entities/teams";

const MAX_CC = 20;

export interface ICreateTicketFormValues {
  subject: string;
  requesterName: string;
  requesterEmail: string;
  assigneeId: string;
  assignedTeamId: string;
  priority: string;
  status: TicketStatus;
  tags: string[];
  richTextPresent: boolean;
  message: Descendant[];
  cannedResponseIds: string[];
  isPrivate?: boolean;
  transactionID?: string;
  peopleInTheLoopInput: string;
  peopleInTheLoop: string[];
}

export const getInitialCreateTicketFormValues = (
  defaultTeamId: string,
  agents: IAgent[],
  teams: ITeam[]
): ICreateTicketFormValues => {
  const ticketDrafts = getItem(SESSION_STORAGE_DRAFT_KEY) || {};
  const draft: IDraft = ticketDrafts[NEW_TICKET_TEMP_ID] || {};

  return {
    subject: draft.subject || "",
    requesterName: draft.requesterName || "",
    requesterEmail: draft.requesterEmail || "",
    assigneeId: draft.assigneeId && agentExists(agents, draft.assigneeId) ? draft.assigneeId : "",
    assignedTeamId:
      draft.assignedTeamId && teamExists(teams, draft.assignedTeamId) ? draft.assignedTeamId : defaultTeamId,
    priority: draft.priority || TicketPriority.Medium.toString(),
    status: draft.status || TicketStatus.Pending,
    tags: draft.tags || [],
    isPrivate: draft.isPrivate || false,
    richTextPresent: false,
    message: (draft.message as Descendant[]) || EMPTY_EDITOR_STATE,
    cannedResponseIds: draft.cannedResponseIds || [],
    transactionID: undefined,
    peopleInTheLoop: draft.peopleInTheLoop || [],
    peopleInTheLoopInput: "",
  };
};

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const validateTicketAddSubject = (value: string) => (value ? undefined : "Enter required subject");

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const validateTicketAddRequesterEmail = (peopleInTheLoop?: string[]) => (value: string) => {
  if (!value) {
    return "Enter requester email address";
  }

  if (!isEmail(value)) {
    return "Enter correct email address";
  }

  if (peopleInTheLoop?.includes(value.toLowerCase())) {
    return "This email address is already added to the loop";
  }
};

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const validatePeopleInTheLoopEmail = (peopleInTheLoop: string[], requesterEmail?: string) => (value: string) => {
  if (!value) {
    return;
  }

  if (!isEmail(value)) {
    return "Enter correct email address";
  }

  if (peopleInTheLoop.length >= MAX_CC) {
    return `You can add up to ${MAX_CC} people to the loop`;
  }

  if (peopleInTheLoop.includes(value)) {
    return "This email address is already added to the loop";
  }

  if (value === requesterEmail) {
    return "This is the requester email address";
  }
};

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const scrollToFirstError = (errors: Record<string, string>) => {
  const firstErrorKey = Object.keys(errors)[0];
  if (firstErrorKey) {
    const firstErrorElement = document.querySelector(`[name="${firstErrorKey}"]`);
    if (firstErrorElement) {
      firstErrorElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }
};

export const getTeamReplyAddress = (
  team: ITeam,
  replyAddresses: Record<string, IReplyAddress>,
  licenseId: number
): string =>
  team.replyAddressID ? replyAddresses[team.replyAddressID]?.email : `${licenseId}@${config.defaultMailboxSuffix}`;
